/**
 * Category Error Modal - Phase 3/3
 * Shows when user tries to add a product from a different category
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useComparator } from '../context/ComparatorContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FaExclamationTriangle, FaExchangeAlt, FaTimes } from 'react-icons/fa';

export default function ComparatorCategoryErrorModal() {
  const { i18n } = useTranslation();
  const { 
    showCategoryErrorModal, 
    pendingProduct, 
    comparatorData,
    replaceWithNewCategory,
    cancelPendingProduct 
  } = useComparator();

  if (!showCategoryErrorModal || !pendingProduct) {
    return null;
  }

  return (
    <Dialog open={showCategoryErrorModal} onOpenChange={cancelPendingProduct}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-yellow-600">
            <FaExclamationTriangle />
            {i18n.language === 'fr' ? 'Comparaison impossible' : 'Comparison not possible'}
          </DialogTitle>
          <DialogDescription className="pt-4 space-y-4">
            <p className="text-gray-700">
              {i18n.language === 'fr' 
                ? 'Vous ne pouvez comparer que des produits de la même catégorie.'
                : 'You can only compare products from the same category.'}
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {/* Current product */}
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-[#635BFF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  {pendingProduct.product.images?.[0] ? (
                    <img 
                      src={pendingProduct.product.images[0]} 
                      alt=""
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <span className="text-xl">📦</span>
                  )}
                </div>
                <div>
                  <p className="font-medium text-[#0A2540] text-sm">
                    {i18n.language === 'fr' ? 'Produit en cours :' : 'Current product:'}
                  </p>
                  <p className="text-sm">{pendingProduct.product.name_fr || pendingProduct.product.name}</p>
                  <p className="text-xs text-[#635BFF]">
                    {i18n.language === 'fr' ? 'Catégorie :' : 'Category:'} {pendingProduct.category.name_fr || pendingProduct.category.name}
                  </p>
                </div>
              </div>
              
              <div className="border-t pt-3">
                {/* Comparator content */}
                <p className="font-medium text-[#0A2540] text-sm mb-2">
                  {i18n.language === 'fr' ? 'Comparateur actuel :' : 'Current comparator:'}
                </p>
                <p className="text-sm text-gray-600">
                  {comparatorData.products.length} {i18n.language === 'fr' ? 'produit(s) de' : 'product(s) from'}{' '}
                  <span className="font-medium text-[#635BFF]">{comparatorData.category_name}</span>
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-500">
              {i18n.language === 'fr' 
                ? 'Que voulez-vous faire ?'
                : 'What would you like to do?'}
            </p>
          </DialogDescription>
        </DialogHeader>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={cancelPendingProduct}
            className="w-full sm:w-auto"
          >
            <FaTimes className="mr-2" />
            {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
          </Button>
          <Button
            onClick={replaceWithNewCategory}
            className="w-full sm:w-auto bg-[#635BFF] hover:bg-[#5248d9]"
          >
            <FaExchangeAlt className="mr-2" />
            {i18n.language === 'fr' ? 'Remplacer le comparateur' : 'Replace comparator'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
